import { createContext, useCallback, useContext, useMemo, useReducer, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isMockEnabled } from '@/mocks';
import { useOptionalPassbook } from '@/shared/lib/passbook';
import { channelQuotaKey } from '@/features/channel-quota/api/billing';
import {
  applyScenario,
  moduleGateFor,
  READY_STATE,
  resourceState,
  shouldRetryMarketing,
  toMarketingError,
  useDemoScenario,
  type DemoScenario,
  type ModuleGate,
  type ResourceState,
} from '@/features/marketing-shared';
import {
  approveEmailCampaign,
  deleteEmailCampaign,
  duplicateEmailTemplate,
  emailCampaignAction,
  emailMarketingKeys,
  fetchEmailCampaigns,
  fetchEmailTemplates,
  fetchSendingIdentities,
  saveEmailTemplate,
} from '../api/email-marketing-api';
import { passbookEmailScope, type EmailScope } from './scope';
import { buildLocalTemplate, duplicateLocalTemplate, prepareTemplateDraft } from './template-draft';
import type { CampaignDraftInput, EmailCampaign, EmailTemplate, SendingIdentity, TemplateDraft } from './types';

/**
 * Estado do módulo de e-mail marketing.
 *
 * Na loja (`scope.kind === 'passbook'`) tudo vem da API do fixe-api via BFF:
 * campanhas, modelos e identidades são queries; as ações são chamadas que
 * atualizam o cache e invalidam o que depende delas (saldo inclusive).
 *
 * Na plataforma (`/plataforma`) só a biblioteca de modelos existe, e ainda em
 * memória: o backend não tem template com `scope: 'platform'`
 * (docs/plataforma-integracao.md). Campanhas não se aplicam lá.
 */

interface EmailMarketingContextValue {
  scope: EmailScope;
  /** Loja do módulo; `null` na plataforma. */
  idPassbook: string | null;
  mockMode: boolean;
  scenario: DemoScenario;
  /** Fecha o módulo inteiro: sem permissão, não contratado ou rota ausente no servidor. */
  gate: ModuleGate | null;
  campaigns: EmailCampaign[];
  campaignsState: ResourceState;
  templates: EmailTemplate[];
  templatesState: ResourceState;
  identities: SendingIdentity[];
  identitiesState: ResourceState;
  duplicateTemplate: (id: string) => Promise<EmailTemplate>;
  saveTemplate: (draft: TemplateDraft) => Promise<{ template: EmailTemplate; forked: boolean }>;
  duplicateCampaign: (id: string) => Promise<EmailCampaign>;
  removeCampaign: (id: string) => Promise<void>;
  pauseCampaign: (id: string) => Promise<EmailCampaign>;
  resumeCampaign: (id: string) => Promise<EmailCampaign>;
  cancelCampaign: (id: string) => Promise<EmailCampaign>;
  /** Cria (ou promove o rascunho) e agenda. 422 vira `MarketingError.blocks`. */
  approveCampaign: (
    input: CampaignDraftInput,
    options?: { allowPartial?: boolean },
  ) => Promise<{ campaign: EmailCampaign; leftOut: number }>;
}

const EmailMarketingContext = createContext<EmailMarketingContextValue | null>(null);

type LocalAction = { type: 'upsert'; template: EmailTemplate } | { type: 'add'; template: EmailTemplate };

function localTemplatesReducer(state: EmailTemplate[], action: LocalAction): EmailTemplate[] {
  if (action.type === 'add') return [action.template, ...state];
  const exists = state.some((item) => item.id === action.template.id);
  return exists ? state.map((item) => (item.id === action.template.id ? action.template : item)) : [action.template, ...state];
}

function upsertFirst<T extends { id: string }>(list: T[] | undefined, item: T): T[] {
  return [item, ...(list ?? []).filter((row) => row.id !== item.id)];
}

function replaceInPlace<T extends { id: string }>(list: T[] | undefined, item: T): T[] {
  if (!list) return [item];
  return list.some((row) => row.id === item.id) ? list.map((row) => (row.id === item.id ? item : row)) : [item, ...list];
}

export function EmailMarketingProvider({
  children,
  scope: scopeProp,
  initialTemplates,
}: {
  children: ReactNode;
  /** Sem `scope`, o módulo roda na loja da rota (`/loja/:id/...`). */
  scope?: EmailScope;
  /** Biblioteca inicial da plataforma (memória da sessão). Ignorada no escopo da loja. */
  initialTemplates?: EmailTemplate[];
}) {
  const passbookCtx = useOptionalPassbook();
  if (!scopeProp && !passbookCtx) {
    throw new Error('EmailMarketingProvider precisa de um escopo ou de uma loja na rota.');
  }
  const scope = useMemo<EmailScope>(
    () =>
      scopeProp ??
      passbookEmailScope(passbookCtx!.idPassbook, passbookCtx!.passbook.organizationName || 'Sua loja'),
    [scopeProp, passbookCtx],
  );
  const live = scope.kind === 'passbook';
  const idPassbook = live ? (scope.idPassbook ?? passbookCtx?.idPassbook ?? null) : null;
  const keyPassbook = idPassbook ?? 'platform';
  const mockMode = isMockEnabled();
  const scenario = useDemoScenario();
  const queryClient = useQueryClient();

  const campaignsQuery = useQuery({
    queryKey: emailMarketingKeys.campaigns(keyPassbook),
    queryFn: () => fetchEmailCampaigns(idPassbook!),
    enabled: Boolean(idPassbook),
    retry: shouldRetryMarketing,
    staleTime: 15_000,
  });
  const templatesQuery = useQuery({
    queryKey: emailMarketingKeys.templates(keyPassbook),
    queryFn: () => fetchEmailTemplates(idPassbook!),
    enabled: Boolean(idPassbook),
    retry: shouldRetryMarketing,
    staleTime: 60_000,
  });
  const identitiesQuery = useQuery({
    queryKey: emailMarketingKeys.identities(keyPassbook),
    queryFn: () => fetchSendingIdentities(idPassbook!),
    enabled: Boolean(idPassbook),
    retry: shouldRetryMarketing,
    staleTime: 5 * 60_000,
  });

  const [localTemplates, dispatchLocal] = useReducer(
    localTemplatesReducer,
    initialTemplates,
    (seed) => structuredClone(seed ?? []),
  );

  const campaigns = useMemo(
    () => (live ? applyScenario(campaignsQuery.data ?? [], resourceState(campaignsQuery, toMarketingError), scenario) : { items: [], state: READY_STATE }),
    [live, campaignsQuery, scenario],
  );
  const templates = useMemo(
    () =>
      live
        ? applyScenario(templatesQuery.data ?? [], resourceState(templatesQuery, toMarketingError), scenario)
        : { items: localTemplates, state: READY_STATE },
    [live, templatesQuery, localTemplates, scenario],
  );
  const identities = useMemo(
    () => (live ? applyScenario(identitiesQuery.data ?? [], resourceState(identitiesQuery, toMarketingError), scenario) : { items: [], state: READY_STATE }),
    [live, identitiesQuery, scenario],
  );

  const gate = useMemo<ModuleGate | null>(() => {
    if (!live) return null;
    if (scenario === 'sem-permissao') return { kind: 'forbidden', message: 'Cenário de demonstração.' };
    if (scenario === 'nao-contratado') return { kind: 'not_contracted', message: 'Cenário de demonstração.' };
    return moduleGateFor(campaigns.state.error);
  }, [live, scenario, campaigns.state.error]);

  const requirePassbook = useCallback(() => {
    if (!idPassbook) throw new Error('Esta ação só existe no módulo da loja.');
    return idPassbook;
  }, [idPassbook]);

  const putCampaign = useCallback(
    (campaign: EmailCampaign, mode: 'replace' | 'prepend') => {
      const key = emailMarketingKeys.campaigns(keyPassbook);
      queryClient.setQueryData<EmailCampaign[]>(key, (old) =>
        mode === 'prepend' ? upsertFirst(old, campaign) : replaceInPlace(old, campaign),
      );
      void queryClient.invalidateQueries({ queryKey: emailMarketingKeys.campaignAll(keyPassbook, campaign.id) });
    },
    [keyPassbook, queryClient],
  );

  const duplicateTemplate = useCallback(
    async (id: string) => {
      if (!live) {
        const source = localTemplates.find((item) => item.id === id);
        if (!source) throw new Error('Modelo não encontrado.');
        const copy = duplicateLocalTemplate(source);
        dispatchLocal({ type: 'add', template: copy });
        return copy;
      }
      const copy = await duplicateEmailTemplate(id, requirePassbook());
      queryClient.setQueryData<EmailTemplate[]>(emailMarketingKeys.templates(keyPassbook), (old) => upsertFirst(old, copy));
      return copy;
    },
    [live, localTemplates, keyPassbook, queryClient, requirePassbook],
  );

  const saveTemplate = useCallback(
    async (draft: TemplateDraft) => {
      const prepared = prepareTemplateDraft(draft);
      if (!live) {
        const source = prepared.id ? localTemplates.find((item) => item.id === prepared.id) : undefined;
        const result = buildLocalTemplate(prepared, source);
        dispatchLocal({ type: 'upsert', template: result.template });
        return result;
      }
      const result = await saveEmailTemplate(requirePassbook(), prepared);
      queryClient.setQueryData<EmailTemplate[]>(emailMarketingKeys.templates(keyPassbook), (old) =>
        result.forked ? upsertFirst(old, result.template) : replaceInPlace(old, result.template),
      );
      return result;
    },
    [live, localTemplates, keyPassbook, queryClient, requirePassbook],
  );

  const duplicateCampaign = useCallback(
    async (id: string) => {
      const copy = await emailCampaignAction('duplicate', id, requirePassbook());
      putCampaign(copy, 'prepend');
      return copy;
    },
    [putCampaign, requirePassbook],
  );

  const removeCampaign = useCallback(
    async (id: string) => {
      await deleteEmailCampaign(id, requirePassbook());
      queryClient.setQueryData<EmailCampaign[]>(emailMarketingKeys.campaigns(keyPassbook), (old) =>
        (old ?? []).filter((row) => row.id !== id),
      );
      void queryClient.invalidateQueries({ queryKey: emailMarketingKeys.campaignAll(keyPassbook, id) });
    },
    [keyPassbook, queryClient, requirePassbook],
  );

  const transition = useCallback(
    async (action: 'pause' | 'resume' | 'cancel', id: string) => {
      const updated = await emailCampaignAction(action, id, requirePassbook());
      putCampaign(updated, 'replace');
      if (action === 'cancel') void queryClient.invalidateQueries({ queryKey: channelQuotaKey(keyPassbook) });
      return updated;
    },
    [keyPassbook, putCampaign, queryClient, requirePassbook],
  );

  const pauseCampaign = useCallback((id: string) => transition('pause', id), [transition]);
  const resumeCampaign = useCallback((id: string) => transition('resume', id), [transition]);
  const cancelCampaign = useCallback((id: string) => transition('cancel', id), [transition]);

  const approveCampaign = useCallback(
    async (input: CampaignDraftInput, options?: { allowPartial?: boolean }) => {
      const passbook = requirePassbook();
      const result = await approveEmailCampaign(passbook, input, options?.allowPartial === true);
      putCampaign(result.campaign, input.id ? 'replace' : 'prepend');
      void queryClient.invalidateQueries({ queryKey: emailMarketingKeys.campaigns(passbook) });
      void queryClient.invalidateQueries({ queryKey: channelQuotaKey(passbook) });
      return result;
    },
    [putCampaign, queryClient, requirePassbook],
  );

  const value = useMemo<EmailMarketingContextValue>(
    () => ({
      scope,
      idPassbook,
      mockMode,
      scenario,
      gate,
      campaigns: campaigns.items,
      campaignsState: campaigns.state,
      templates: templates.items,
      templatesState: templates.state,
      identities: identities.items,
      identitiesState: identities.state,
      duplicateTemplate,
      saveTemplate,
      duplicateCampaign,
      removeCampaign,
      pauseCampaign,
      resumeCampaign,
      cancelCampaign,
      approveCampaign,
    }),
    [
      scope,
      idPassbook,
      mockMode,
      scenario,
      gate,
      campaigns,
      templates,
      identities,
      duplicateTemplate,
      saveTemplate,
      duplicateCampaign,
      removeCampaign,
      pauseCampaign,
      resumeCampaign,
      cancelCampaign,
      approveCampaign,
    ],
  );

  return <EmailMarketingContext.Provider value={value}>{children}</EmailMarketingContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useEmailMarketing(): EmailMarketingContextValue {
  const ctx = useContext(EmailMarketingContext);
  if (!ctx) throw new Error('useEmailMarketing deve ficar dentro do módulo de e-mail marketing.');
  return ctx;
}
